import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, type KasiaGoalTrack } from "@/lib/api";
import { formatAxisMoney } from "@/lib/chartSeriesAxes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn, formatPLN } from "@/lib/utils";

function paceLabel(pace: string): string {
  if (pace === "powyzej") return "powyżej celu";
  if (pace === "ponizej") return "poniżej celu";
  return "blisko celu";
}

function paceColor(pace: string): string {
  if (pace === "powyzej") return "text-emerald-600";
  if (pace === "ponizej") return "text-rose-600";
  return "text-amber-600";
}

function MoneyBag({
  pct,
  className,
}: {
  pct: number;
  className?: string;
}) {
  const fill = Math.max(0, Math.min(100, pct));
  // Worek: clip od dołu (SVG y rośnie w dół)
  const bagTop = 28;
  const bagBottom = 118;
  const bagHeight = bagBottom - bagTop;
  const fillTop = bagBottom - (bagHeight * fill) / 100;

  return (
    <svg
      viewBox="0 0 120 140"
      className={cn("h-40 w-36 shrink-0", className)}
      role="img"
      aria-label={`Worek pieniędzy wypełniony w ${fill.toLocaleString("pl-PL", {
        maximumFractionDigits: 1,
      })} procent`}
    >
      <defs>
        <clipPath id="money-bag-clip">
          <path d="M38 34 C28 48 22 68 24 92 C26 112 40 122 60 122 C80 122 94 112 96 92 C98 68 92 48 82 34 Z" />
        </clipPath>
        <linearGradient id="money-fill" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="hsl(142 55% 38%)" />
          <stop offset="100%" stopColor="hsl(142 48% 52%)" />
        </linearGradient>
      </defs>

      {/* cień */}
      <ellipse cx="60" cy="128" rx="28" ry="5" fill="hsl(var(--muted-foreground) / 0.15)" />

      {/* wypełnienie (clip) */}
      <g clipPath="url(#money-bag-clip)">
        <rect
          x="18"
          y={fillTop}
          width="84"
          height={bagBottom - fillTop + 4}
          fill="url(#money-fill)"
        />
        {fill > 8 && (
          <text
            x="60"
            y={Math.min(bagBottom - 10, fillTop + 22)}
            textAnchor="middle"
            fontSize="11"
            fontWeight="700"
            fill="white"
          >
            {fill.toLocaleString("pl-PL", { maximumFractionDigits: 0 })}%
          </text>
        )}
      </g>

      {/* obrys worka */}
      <path
        d="M38 34 C28 48 22 68 24 92 C26 112 40 122 60 122 C80 122 94 112 96 92 C98 68 92 48 82 34 Z"
        fill="none"
        stroke="hsl(var(--foreground) / 0.55)"
        strokeWidth="2.2"
      />
      {/* sznurek / węzeł */}
      <path
        d="M44 34 Q60 22 76 34"
        fill="none"
        stroke="hsl(var(--foreground) / 0.55)"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <circle cx="60" cy="28" r="6" fill="hsl(38 70% 48%)" stroke="hsl(var(--foreground) / 0.4)" strokeWidth="1.2" />
      <text
        x="60"
        y="31.5"
        textAnchor="middle"
        fontSize="8"
        fontWeight="700"
        fill="white"
      >
        zł
      </text>
    </svg>
  );
}

export default function GoalTrackChart({
  data,
  className,
}: {
  data: KasiaGoalTrack;
  className?: string;
}) {
  const month = data.month;
  const yearMonth = data.year_month || "";
  const recentDays = month.recent_days || 7;
  const queryClient = useQueryClient();
  const [goalDraft, setGoalDraft] = useState(String(Math.round(data.goal_monthly_pln)));
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setGoalDraft(String(Math.round(data.goal_monthly_pln)));
  }, [data.goal_monthly_pln, yearMonth]);

  const saveGoal = useMutation({
    mutationFn: async () => {
      const parsed = Number(String(goalDraft).replace(/\s/g, "").replace(",", "."));
      if (!Number.isFinite(parsed) || parsed < 1) {
        throw new Error("Podaj sensowną kwotę celu");
      }
      if (!yearMonth) throw new Error("Brak year_month");
      return api.setMonthlyGoal(yearMonth, parsed);
    },
    onSuccess: () => {
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1800);
      void queryClient.invalidateQueries({ queryKey: ["kasia-summary"] });
    },
  });

  const chartData = useMemo(() => {
    const points = data.daily_progress ?? [];
    return points.map((p) => ({
      day: p.day,
      label: String(p.day),
      cumulative: p.cumulative,
      target: p.target_cumulative,
      projected: p.projected_cumulative ?? null,
      is_future: p.is_future,
    }));
  }, [data.daily_progress]);

  const goalChanged =
    Number(String(goalDraft).replace(/\s/g, "").replace(",", ".")) !==
    Math.round(data.goal_monthly_pln);

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">
              Worek pieniędzy · {month.label_pl}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Postęp miesiąca względem Twojego targetu
              {data.goal_is_custom ? "" : " (domyślnie 30 000 zł)"}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="mb-1 block text-[11px] text-muted-foreground">
                Cel miesiąca (zł)
              </label>
              <Input
                type="number"
                min={1}
                step={100}
                className="h-9 w-32 tabular-nums"
                value={goalDraft}
                onChange={(e) => setGoalDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && goalChanged) saveGoal.mutate();
                }}
              />
            </div>
            <Button
              size="sm"
              variant={goalChanged ? "default" : "outline"}
              disabled={!goalChanged || saveGoal.isPending || !yearMonth}
              onClick={() => saveGoal.mutate()}
            >
              {saveGoal.isPending ? "Zapis…" : "Zapisz cel"}
            </Button>
          </div>
        </div>
        {(saveGoal.isError || savedFlash) && (
          <p
            className={cn(
              "text-xs",
              saveGoal.isError ? "text-rose-600" : "text-emerald-600",
            )}
          >
            {saveGoal.isError
              ? saveGoal.error instanceof Error
                ? saveGoal.error.message
                : "Nie udało się zapisać celu"
              : "Cel zapisany dla tego miesiąca"}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-stretch">
          <div className="flex flex-col items-center justify-center rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
            <MoneyBag pct={month.pct_of_goal} />
            <p className="mt-1 text-center text-xs text-muted-foreground">
              {formatPLN(month.revenue_mtd)} z {formatPLN(data.goal_monthly_pln)}
            </p>
          </div>

          <div className="grid flex-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-border/50 p-3">
              <p className="text-xs text-muted-foreground">Od początku miesiąca</p>
              <p className="text-lg font-semibold tabular-nums">
                {formatPLN(month.revenue_mtd)}
              </p>
              <p className="text-xs text-muted-foreground">
                dzień {month.days_elapsed}/{month.days_in_month}
              </p>
            </div>
            <div className="rounded-lg border border-border/50 p-3">
              <p className="text-xs text-muted-foreground">% celu</p>
              <p className="text-lg font-semibold tabular-nums">
                {month.pct_of_goal.toLocaleString("pl-PL", {
                  maximumFractionDigits: 1,
                })}
                %
              </p>
              <p className="text-xs text-muted-foreground">
                liniowy rytm:{" "}
                {(
                  (month.days_elapsed / Math.max(1, month.days_in_month)) *
                  100
                ).toLocaleString("pl-PL", { maximumFractionDigits: 0 })}
                %
              </p>
            </div>
            <div className="rounded-lg border border-border/50 p-3">
              <p className="text-xs text-muted-foreground">
                Predykcja (ostatnie {recentDays} dni)
              </p>
              <p
                className={cn(
                  "text-lg font-semibold tabular-nums",
                  paceColor(month.pace_vs_goal),
                )}
              >
                {formatPLN(month.projected_pln)}
              </p>
              <p className="text-xs text-muted-foreground">
                śr. {formatPLN(month.avg_daily_recent ?? 0)}/dzień ·{" "}
                {paceLabel(month.pace_vs_goal)}
              </p>
              {month.projected_lookback_pln != null && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  vs średnia {month.lookback_weeks} tyg.:{" "}
                  {formatPLN(month.projected_lookback_pln)}
                </p>
              )}
            </div>
          </div>
        </div>

        {chartData.length === 0 ? (
          <p className="text-sm text-muted-foreground">Brak danych dziennych.</p>
        ) : (
          <div>
            <p className="mb-2 text-xs text-muted-foreground">
              Skumulowany przychód vs liniowy target · kropkowana = predykcja z
              ostatnich {recentDays} dni
            </p>
            <div className="h-[260px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="label"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    interval="preserveStartEnd"
                    tickFormatter={(v) => (Number(v) % 5 === 1 || Number(v) === month.days_in_month ? String(v) : "")}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    tickFormatter={formatAxisMoney}
                    width={44}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(value, name) => {
                      if (value == null || value === "") return ["brak", String(name)];
                      const label =
                        name === "cumulative"
                          ? "Skumulowane"
                          : name === "target"
                            ? "Target liniowy"
                            : name === "projected"
                              ? `Predykcja (${recentDays} dni)`
                              : String(name);
                      return [formatPLN(Number(value) || 0), label];
                    }}
                    labelFormatter={(label) => `Dzień ${label}`}
                  />
                  <ReferenceLine
                    y={data.goal_monthly_pln}
                    stroke="hsl(var(--muted-foreground))"
                    strokeDasharray="4 4"
                    label={{
                      value: "cel",
                      position: "insideTopRight",
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 10,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="target"
                    name="target"
                    stroke="hsl(var(--muted-foreground) / 0.55)"
                    strokeWidth={1.5}
                    strokeDasharray="5 4"
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="projected"
                    name="projected"
                    stroke="hsl(38 80% 42%)"
                    strokeWidth={2}
                    strokeDasharray="2 4"
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="cumulative"
                    name="cumulative"
                    stroke="hsl(var(--brand))"
                    strokeWidth={2.4}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
